import { Button } from '@savvy-kit/components/button';
import { Skeleton } from '@savvy-kit/components/skeleton';
import { Text } from '@savvy-kit/foundations/text';
import { cn } from '@savvy-kit/lib/utils';
import Banner from '@savvy-kit/patterns/banner';
import { Plus, RotateCw } from 'lucide-react';
import ConnectAccountsNudgeCard from 'src/components/net-worth/views/connect-accounts-nudge-card';
import { usePageTitle } from 'src/hooks/use-page-title';

import { useTrackPageView } from '@savvy/deprecated-shared/tracking/use-track-page-view';

import {
  CLIENT_PORTAL_BANNER_TITLE_WRAP_CLASS,
  CLIENT_PORTAL_PAGE_HEADING_CLASS,
  CLIENT_PORTAL_PAGE_PADDING_CLASS,
} from '../common/constants';
import { PortalCard } from '../common/portal-section';
import { AccountsAddAccountDialog } from './accounts-add-account-dialog';
import { AccountsDisclosure } from './accounts-disclosure';
import { AccountsGroupSection } from './accounts-group-section';
import { AccountsNetWorthCard } from './accounts-net-worth-card';
import { AccountsRenameDialog } from './accounts-rename-dialog';
import { AccountsStalenessBanners } from './accounts-staleness-banners';
import { accountsSummary, hasSummaryLines } from './accounts-summary';
import { AccountsSummaryCard, AccountsSummaryCardSkeleton } from './accounts-summary-card';
import { AccountsUpdateBalanceDialog } from './accounts-update-balance-dialog';
import {
  ACCOUNTS_CONNECT_LABEL,
  ACCOUNTS_CONNECT_NUDGE_DESCRIPTION,
  ACCOUNTS_CONNECT_NUDGE_LABEL,
  ACCOUNTS_EMPTY_DESCRIPTION,
  ACCOUNTS_EMPTY_TITLE,
  ACCOUNTS_ERROR_DESCRIPTION,
  ACCOUNTS_ERROR_TITLE,
  ACCOUNTS_RETRY_LABEL,
  ACCOUNTS_SKELETON_GROUPS,
  ACCOUNTS_TITLE,
} from './constants';
import { useClientPortalAccounts } from './hooks/use-client-portal-accounts';
import { accountBalanceDraft, transferAccountSections } from './utils';

const SKELETON_GROUPS = Array.from(
  { length: ACCOUNTS_SKELETON_GROUPS },
  (_unused, index) => `accounts-placeholder-${index}`,
);

const SUMMARY_ROW_CLASS = 'grid grid-cols-1 items-start gap-6 lg:grid-cols-3';

function AccountsSkeleton() {
  return (
    <div className="flex flex-col gap-8" data-testid="client-portal-accounts-loading">
      <div className={SUMMARY_ROW_CLASS}>
        <Skeleton className="h-96 w-full lg:col-span-2" />
        <AccountsSummaryCardSkeleton />
      </div>
      {SKELETON_GROUPS.map((group) => (
        <div key={group} className="flex flex-col gap-3">
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-32 w-full" />
        </div>
      ))}
    </div>
  );
}

function AccountsEmptyState({
  canConnect,
  onConnect,
}: {
  readonly canConnect: boolean;
  readonly onConnect: () => void;
}) {
  return (
    <PortalCard>
      <div className="flex flex-col items-center gap-3 py-6 text-center">
        <Text variant="h4" color="dark">
          {ACCOUNTS_EMPTY_TITLE}
        </Text>
        <Text variant="body-sm">{ACCOUNTS_EMPTY_DESCRIPTION}</Text>
        {canConnect && (
          <Button className="mt-2" onClick={onConnect}>
            <Plus size={14} aria-hidden="true" />
            {ACCOUNTS_CONNECT_LABEL}
          </Button>
        )}
      </div>
    </PortalCard>
  );
}

export function ClientPortalAccounts() {
  const accounts = useClientPortalAccounts();
  usePageTitle(ACCOUNTS_TITLE);
  useTrackPageView();
  const showEmptyState = !accounts.loading && !accounts.failed && accounts.groups.length === 0;
  const summary = accountsSummary(accounts.groups);
  const showSummary = !accounts.transferring && hasSummaryLines(summary);
  const showSlot = accounts.showConnectNudge || showSummary;
  const sections = accounts.transferring ? transferAccountSections(accounts.groups) : accounts.groups;

  return (
    <div className={cn(CLIENT_PORTAL_PAGE_PADDING_CLASS, 'pt-8')}>
      <div className="mb-6 flex items-start justify-between gap-3">
        <Text variant="h1" className={CLIENT_PORTAL_PAGE_HEADING_CLASS}>
          {ACCOUNTS_TITLE}
        </Text>
        {accounts.canConnect && !showEmptyState && (
          <Button
            onClick={() => {
              accounts.openConnect();
            }}
          >
            <Plus size={14} aria-hidden="true" />
            {ACCOUNTS_CONNECT_LABEL}
          </Button>
        )}
      </div>

      {!accounts.loading && !accounts.failed && (
        <AccountsStalenessBanners
          connections={accounts.staleConnections}
          onDismiss={accounts.dismissStaleConnections}
          onReconnected={accounts.onReconnected}
        />
      )}

      {accounts.loading && <AccountsSkeleton />}

      {!accounts.loading && accounts.failed && (
        <Banner
          variant="destructive"
          className={CLIENT_PORTAL_BANNER_TITLE_WRAP_CLASS}
          title={ACCOUNTS_ERROR_TITLE}
          description={ACCOUNTS_ERROR_DESCRIPTION}
          actionButton={
            <Button variant="secondary" size="sm" onClick={accounts.retry}>
              <RotateCw size={14} aria-hidden="true" />
              {ACCOUNTS_RETRY_LABEL}
            </Button>
          }
        />
      )}

      {showEmptyState && (
        <AccountsEmptyState
          canConnect={accounts.canConnect}
          onConnect={() => {
            accounts.openConnect();
          }}
        />
      )}

      {!accounts.loading && !accounts.failed && accounts.groups.length > 0 && (
        <div className="flex flex-col gap-8">
          <div className={SUMMARY_ROW_CLASS}>
            <div className={showSlot ? 'lg:col-span-2' : 'lg:col-span-3'}>
              <AccountsNetWorthCard
                netWorth={accounts.netWorth}
                historyGroups={accounts.historyGroups}
                historyLoading={accounts.historyLoading}
                historyFailed={accounts.historyFailed}
                onRetryHistory={accounts.retryHistory}
                balancesUpdatedLabel={accounts.balancesUpdatedLabel}
                transferring={accounts.transferring}
                destinationCustodian={accounts.destinationCustodian}
                returns={accounts.netWorthReturns}
                returnsLoading={accounts.netWorthReturnsLoading}
              />
            </div>
            {accounts.showConnectNudge && (
              <ConnectAccountsNudgeCard
                className="h-full"
                description={ACCOUNTS_CONNECT_NUDGE_DESCRIPTION}
                connectLabel={ACCOUNTS_CONNECT_NUDGE_LABEL}
                onConnect={() => {
                  accounts.openConnect();
                }}
                onDismiss={accounts.dismissConnectNudge}
              />
            )}
            {showSummary && <AccountsSummaryCard summary={summary} netWorth={accounts.netWorth} />}
          </div>
          {sections.map((group) => (
            <AccountsGroupSection
              key={group.id}
              group={group}
              pendingCustomNameId={accounts.pendingCustomNameId}
              canConnect={accounts.canConnect}
              onConnect={accounts.openConnect}
              canManageRows={accounts.canManageRows}
              onRefreshBalances={accounts.refreshBalances}
              onRenameRow={accounts.openRename}
              onUpdateRowBalance={accounts.openBalanceUpdate}
              onRemoveRow={accounts.removeRow}
              onToggleRowInNetWorth={accounts.toggleRowInNetWorth}
              onDropRowOnto={accounts.dropRowOnto}
              onNudgeRow={accounts.nudgeRow}
            />
          ))}
          <AccountsDisclosure />
        </div>
      )}

      {accounts.renamingRow !== null && (
        <AccountsRenameDialog row={accounts.renamingRow} onRename={accounts.renameRow} onClose={accounts.closeRename} />
      )}

      {accounts.balanceRow !== null && (
        <AccountsUpdateBalanceDialog
          accountId={accounts.balanceRow.id}
          accountName={accounts.balanceRow.name}
          currentBalance={accountBalanceDraft(accounts.balanceRow.balance)}
          onUpdated={accounts.onBalanceUpdated}
          onClose={accounts.closeBalanceUpdate}
        />
      )}

      {accounts.connectOpen && (
        <AccountsAddAccountDialog
          initialItemType={accounts.connectItemType}
          feedOnly={accounts.connectFeedOnly}
          onLinked={accounts.onConnected}
          onClose={accounts.closeConnect}
        />
      )}
    </div>
  );
}
